import type { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { SearchSidebar } from '../components/SearchSidebar';
import { SearchAutocomplete, type FacetCategory } from '../components/SearchAutocomplete';
import { useDebouncedUrlSearchTerm } from '../hooks/useDebouncedUrlSearchTerm';
import { useFacets } from '../hooks/useFacets';
import type { RiftboundFilter } from '../types';

interface SearchSidebarBoundProps {
  fixedSetCode?: string;
  /** Optional block above Search (e.g. team owned-by pills). */
  leading?: ReactNode;
}

function withFixedSet(filter: RiftboundFilter, fixedSetCode?: string): RiftboundFilter {
  return fixedSetCode ? { ...filter, set: [fixedSetCode] } : filter;
}

export function SearchSidebarBound({ fixedSetCode, leading }: SearchSidebarBoundProps = {}) {
  const { localTerm, setLocalTerm, filter: urlFilter, setFilter: setUrlFilter, handleCommitSearch } =
    useDebouncedUrlSearchTerm();
  const filter = withFixedSet(urlFilter, fixedSetCode);

  const setFilter = (next: RiftboundFilter) => {
    setUrlFilter(withFixedSet(next, fixedSetCode));
  };

  const handleCommitFacet = (category: FacetCategory, value: string) => {
    const current = (filter[category] as string[]) ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setFilter({ ...filter, [category]: next });
  };

  return (
    <SearchSidebar
      filter={filter}
      setFilter={setFilter}
      searchTerm={localTerm}
      setSearchTerm={setLocalTerm}
      hideSetFilter={Boolean(fixedSetCode)}
      onCommitSearch={handleCommitSearch}
      onCommitFacet={handleCommitFacet}
      leading={leading}
    />
  );
}

/** Inline search bar rendered above the card grid on mobile viewports. */
export function SearchInlineBound() {
  const { localTerm, setLocalTerm, handleCommitSearch, handleCommitFacet } =
    useDebouncedUrlSearchTerm();
  const facets = useFacets();

  return (
    <Box display={{ base: 'block', md: 'none' }} mb={2}>
      <SearchAutocomplete
        value={localTerm}
        onChange={setLocalTerm}
        onCommit={handleCommitSearch}
        onCommitFacet={handleCommitFacet}
        facets={{
          keywords: facets.keywords,
          cardTypes: facets.cardTypes,
          supertypes: facets.supertypes,
          regions: facets.regions,
          subtypes: facets.subtypes,
          rarities: facets.rarities,
          artists: facets.artists,
        }}
      />
    </Box>
  );
}
